import {
  DataObjectGroupsWithDescriptionsMapById,
  DataObjectGroupType,
  DataObjectMiniDto,
  ISODate,
  keepStaleInMinutes,
  Spinner,
  useDataObjectGroupsWithDescriptionsQuery,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { keepPreviousData } from '@tanstack/react-query';
import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { useConformDateToValueDate } from '../../../common/useConformDateToValueDate';
import { isDateBetween } from '../../input-data/table/utils';
import { NotFound } from '../../NotFound';
import { SampleDataObjectType } from '../SampleSearch';
import { SampleContextProps, SampleContextValue } from './SampleContext.types';

export const SampleContext = createContext<SampleContextValue | undefined>(
  undefined,
);

function isActive(description: DataObjectMiniDto, date: string) {
  return (
    description.activePeriods?.some(
      (period) =>
        date &&
        isDateBetween(
          date as ISODate,
          period.validFrom as ISODate | null,
          period.validTo as ISODate | null,
        ),
    ) ?? false
  );
}

function getGroupWithActiveDescriptions(
  treeData: DataObjectGroupsWithDescriptionsMapById | undefined,
  id: number,
  date: string,
) {
  const groupData = treeData?.get(id);

  if (!groupData) {
    return groupData;
  }

  return {
    ...groupData,
    descriptions: groupData?.descriptions
      .filter((description) => isActive(description, date))
      .toSorted((a, b) => a.code.localeCompare(b.code)),
  };
}

function findGroupOrDescription(
  treeData: DataObjectGroupsWithDescriptionsMapById | undefined,
  id: number,
  dataObjectType: SampleDataObjectType,
  date: string,
  groupMemberId: number | undefined,
  replaceSelected: (selectedId?: number) => void,
  requireGroupMember: boolean,
) {
  let selectedGroupMemberId = groupMemberId;
  const groupOrDescription =
    dataObjectType === 'group'
      ? getGroupWithActiveDescriptions(treeData, id, date)
      : treeData
          ?.get(null)
          ?.descriptions.find((description) => description.id === id);

  if (
    dataObjectType === 'group' &&
    (requireGroupMember || selectedGroupMemberId)
  ) {
    let hasDescription = false;
    let descriptions: DataObjectMiniDto[] = [];
    if (groupOrDescription && 'descriptions' in groupOrDescription) {
      descriptions = groupOrDescription.descriptions;
      hasDescription = descriptions.some(
        (description) => description.id === groupMemberId,
      );
    }
    if (!hasDescription) {
      const id = descriptions[0]?.id;
      replaceSelected(id);
      selectedGroupMemberId = id;
    }
  }

  return { groupOrDescription, selectedGroupMemberId };
}

function useReplaceSelectedQueryParam() {
  const [, setQueryParams] = useQueryParams();

  return useCallback(
    (selectedId?: number) => {
      setQueryParams({ groupMemberId: selectedId });
    },
    [setQueryParams],
  );
}

export const SampleContextProvider: FC<SampleContextProps> = ({
  id,
  date,
  periodSkipLastValueDate,
  dataObjectType,
  groupMemberId,
  sampleView,
  children,
}) => {
  const { data: treeData, isLoading: isLoadingTreeData } =
    useDataObjectGroupsWithDescriptionsQuery(
      { groupType: DataObjectGroupType.I },
      {
        placeholderData: keepPreviousData,
        ...keepStaleInMinutes(1),
      },
    );

  const conformedDate = useConformDateToValueDate({
    id,
    date,
    periodSkipLastValueDate,
    treeData,
    dataObjectType,
  });

  if (isLoadingTreeData) {
    return <Spinner />;
  }

  if (!conformedDate || !treeData) {
    return <NotFound />;
  }

  return (
    <SampleContextProviderInner
      id={id}
      date={conformedDate}
      periodSkipLastValueDate={periodSkipLastValueDate}
      dataObjectType={dataObjectType}
      groupMemberId={groupMemberId}
      sampleView={sampleView}
      treeData={treeData}
    >
      {children}
    </SampleContextProviderInner>
  );
};

const SampleContextProviderInner: FC<
  SampleContextProps & { treeData: DataObjectGroupsWithDescriptionsMapById }
> = ({
  id,
  date,
  dataObjectType,
  groupMemberId,
  sampleView,
  children,
  treeData,
}) => {
  const replaceSelected = useReplaceSelectedQueryParam();
  const value = useMemo(
    () => ({
      ...findGroupOrDescription(
        treeData,
        id,
        dataObjectType,
        date,
        groupMemberId,
        replaceSelected,
        !sampleView,
      ),
      groupOrDescriptionId: id,
      date,
      sampleType: dataObjectType,
      sampleView,
    }),
    [
      dataObjectType,
      date,
      groupMemberId,
      id,
      replaceSelected,
      sampleView,
      treeData,
    ],
  );
  return (
    <SampleContext.Provider value={value}>{children}</SampleContext.Provider>
  );
};

export const useSampleContext = () => {
  const context = useContext(SampleContext);

  if (context === undefined) {
    throw new Error(
      'useSampleContext must be used within a SampleContextProvider',
    );
  }

  return context;
};
